import { useState, useEffect } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { mascotaService } from '../../services/mascotaService'
import MobileCard, { MobileCardImage, MobileCardContent } from '../../components/ui/MobileCard'
import CategoryFilters from '../../components/ui/CategoryFilters'
import { Heart, MapPin, Calendar, Mars, Venus, Cat, Dog, Bird, Rabbit } from 'lucide-react'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import { normalizeFotoUrl } from '../../utils/avatarHelpers'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

export default function MascotasMobile() {
  const { isDueno } = usePermissions()
  const [mascotas, setMascotas] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  // Exponer función de búsqueda para el header
  useEffect(() => {
    const handleSearchChange = (query) => {
      setSearchQuery(query)
    }
    window.mobileSearchHandler = handleSearchChange
    return () => {
      delete window.mobileSearchHandler
    }
  }, [])

  useEffect(() => {
    fetchMascotas()
  }, [isDueno])

  const fetchMascotas = async () => {
    try {
      setLoading(true)
      let response
      if (isDueno) {
        response = await mascotaService.getMascotasByDueno()
      } else {
        response = await mascotaService.getAll()
      }

      setMascotas(extractArray(response))
    } catch (error) {
      console.error('Error fetching mascotas:', error)
      setMascotas([])
    } finally {
      setLoading(false)
    }
  }

  const filteredMascotas = mascotas.filter(mascota => {
    const especie = (mascota.especie || '').toLowerCase()
    const matchesCategory = selectedCategory === 'all'
      || (selectedCategory === 'gatos' && especie.includes('gato'))
      || (selectedCategory === 'perros' && especie.includes('perro'))
      || (selectedCategory === 'aves' && (especie.includes('ave') || especie.includes('pajaro') || especie.includes('pájaro')))
      || (selectedCategory === 'conejos' && especie.includes('conejo'))

    const matchesSearch = !searchQuery || 
      mascota.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mascota.raza?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mascota.especie?.toLowerCase().includes(searchQuery.toLowerCase())
    
    return matchesCategory && matchesSearch
  })

  const getMascotaImage = (mascota) => {
    const source = mascota?.foto || mascota?.avatar_url
    if (source) return normalizeFotoUrl(source)
    return '/placeholder-pet.svg'
  }

  const calculateAge = (fechaNacimiento) => {
    if (!fechaNacimiento) return 'Age not available'
    const birth = new Date(fechaNacimiento)
    const today = new Date()
    let years = today.getFullYear() - birth.getFullYear()
    let months = today.getMonth() - birth.getMonth()
    
    if (months < 0) {
      years--
      months += 12
    }
    
    if (years > 0) {
      if (months >= 6) {
        return `${years}.5 años`
      }
      return `${years} ${years === 1 ? 'año' : 'años'}`
    }
    return `${months} ${months === 1 ? 'mes' : 'meses'}`
  }

  if (loading) {
    return <LoadingState message="Cargando mascotas..." />
  }

  if (filteredMascotas.length === 0) {
    return (
      <div className="px-4 py-6">
        <EmptyState
          icon={Heart}
          title="No hay mascotas"
          message={searchQuery ? "No se encontraron mascotas con ese criterio" : "Aún no tienes mascotas registradas"}
        />
      </div>
    )
  }

  return (
    <div className="min-h-full bg-transparent pb-6">
      <div className="px-4 pt-3 pb-2">
        <div className="rounded-[26px] border border-pastel-pink bg-gradient-to-r from-white to-[#f8fbf4] shadow-soft p-4 animate-fade-in">
          <p className="text-[11px] uppercase tracking-wide text-pastel-lavender font-semibold">Mascotas</p>
          <h2 className="text-[24px] leading-none font-semibold text-pastel-ink mt-1">Mis compañeros</h2>
          <p className="text-xs text-slate-500 mt-1">Tienes {filteredMascotas.length} mascota(s) visible(s)</p>
        </div>
      </div>
      {/* Category Filters */}
      <CategoryFilters
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        categories={[
          { id: 'all', name: 'Todos', icon: Heart },
          { id: 'gatos', name: 'Gatos', icon: Cat },
          { id: 'perros', name: 'Perros', icon: Dog },
          { id: 'aves', name: 'Aves', icon: Bird },
          { id: 'conejos', name: 'Conejos', icon: Rabbit },
        ]}
      />

      {/* Mascotas Grid */}
      <div className="px-4 pb-6 space-y-3">
        {filteredMascotas.map((mascota) => (
          <MobileCard
            key={mascota.id_mascota || mascota.idMascota}
            to={`/mascotas/${mascota.id_mascota || mascota.idMascota}`}
            className="cursor-pointer rounded-[24px] transition-all duration-200 hover:shadow-soft"
          >
            <MobileCardContent className="p-3.5">
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-2xl bg-pastel-cream border border-pastel-pink overflow-hidden shrink-0">
                  <MobileCardImage
                    src={getMascotaImage(mascota)}
                    alt={mascota.nombre || 'Mascota'}
                    className="h-full w-full !aspect-auto rounded-2xl"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center space-x-1.5">
                    <h3 className="text-base font-semibold text-pastel-ink truncate">
                      {mascota.nombre || 'Sin nombre'}
                    </h3>
                    {mascota.sexo === 'M' || mascota.sexo === 'MACHO' ? (
                      <Mars className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                    ) : mascota.sexo === 'H' || mascota.sexo === 'HEMBRA' ? (
                      <Venus className="w-3.5 h-3.5 text-pink-500 shrink-0" />
                    ) : null}
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {mascota.raza || 'Raza no especificada'}
                    {(() => {
                      const tr = mascota.tipo_raza || mascota.tipoRaza
                      if (!tr) return null
                      const lbl = { pura: 'Pura', cruza: 'Cruza', mixto: 'Mixto' }
                      return <span className="text-slate-400"> · {lbl[tr] || tr}</span>
                    })()}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{calculateAge(mascota.fecha_nacimiento || mascota.fechaNacimiento)}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{mascota.distancia || '0 km'}</span>
                    </span>
                  </div>
                </div>
              </div>
            </MobileCardContent>
          </MobileCard>
        ))}
      </div>
    </div>
  )
}
